import { useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { IconClose } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import {
  ResizeHandle,
  useResizableDrawer,
} from "../../../hooks/useResizableDrawer";
import {
  CANCELLATION_REASON_LABELS,
  CANCELLATION_REQUEST_REASONS,
  CancellationRequestReason,
} from "../../../types/cancellation";

type Props = {
  leadUlid: string;
  onClose: () => void;
  onApplied: () => void;
};

const today = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function CancellationRequestDrawer({
  leadUlid,
  onClose,
  onApplied,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();

  const [reason, setReason] = useState<CancellationRequestReason | "">("");
  const [requestDate, setRequestDate] = useState<string>(today());
  const [communicationDate, setCommunicationDate] = useState<string>(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { width, startResize } = useResizableDrawer({ initial: 520, min: 360 });

  const submit = async () => {
    if (submitting) return;
    if (!reason) {
      setError("Selecciona un motivo");
      return;
    }
    if (!requestDate) {
      setError("La fecha de solicitud es obligatoria");
      return;
    }
    if (!communicationDate) {
      setError("La fecha de comunicación es obligatoria");
      return;
    }
    const ok = await confirm({
      title: "Crear solicitud de cancelación",
      message:
        "Esto creará la solicitud en estado borrador. Después, desde la vista de solicitudes, podrás enviarla a Viafirma para que el cliente la firme. ¿Continuar?",
      confirmLabel: "Crear borrador",
      tone: "primary",
    });
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    try {
      await request(
        `/api/admin/operations/leads/${leadUlid}/cancellation-request`,
        {
          method: "POST",
          token,
          body: { reason, requestDate, communicationDate },
        },
      );
      onApplied();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 200) ||
              ((err.body as { error?: string })?.error ?? "")
            }`
          : "No se pudo crear la solicitud",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerShell
      title="Solicitud de cancelación del cliente (Tipo 1)"
      onClose={onClose}
      width={width}
      onStartResize={startResize}
    >
      <p
        style={{
          margin: "0 0 0.85rem",
          fontSize: ".82rem",
          color: "#73879C",
        }}
      >
        Genera la solicitud de cancelación con firma del cliente. El cliente
        recibirá un email con el link de Viafirma; la asesora completa el
        proceso una vez confirmada la firma.
      </p>

      <Field label="Motivo">
        <select
          value={reason}
          onChange={(e) =>
            setReason(e.target.value as CancellationRequestReason | "")
          }
          style={selectStyle}
          disabled={submitting}
        >
          <option value="">— Selecciona un motivo —</option>
          {CANCELLATION_REQUEST_REASONS.map((r) => (
            <option key={r} value={r}>
              {CANCELLATION_REASON_LABELS[r]}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Fecha de solicitud">
        <input
          type="date"
          value={requestDate}
          onChange={(e) => setRequestDate(e.target.value)}
          style={inputStyle}
          disabled={submitting}
        />
      </Field>

      <Field label="Fecha de comunicación">
        <input
          type="date"
          value={communicationDate}
          onChange={(e) => setCommunicationDate(e.target.value)}
          style={inputStyle}
          disabled={submitting}
        />
        <span
          style={{
            fontSize: ".7rem",
            color: "#73879C",
            marginTop: "0.2rem",
          }}
        >
          Es la fecha que se imprime en el PDF y en el email al cliente como
          "fecha efectiva".
        </span>
      </Field>

      {error && (
        <div
          style={{
            marginTop: "0.7rem",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            background: "#fde2e2",
            color: "#b32424",
            fontSize: ".8rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          justifyContent: "flex-end",
          marginTop: "1.1rem",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          style={ghostBtn}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          style={primaryBtn}
        >
          {submitting ? "Creando…" : "Crear borrador"}
        </button>
      </div>
    </DrawerShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: "0.3rem", marginBottom: "0.85rem" }}>
      <span
        style={{
          fontSize: ".72rem",
          color: "#73879C",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function DrawerShell({
  title,
  onClose,
  children,
  width,
  onStartResize,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  width: number;
  onStartResize: (event: React.MouseEvent) => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          height: "100%",
          background: "white",
          boxShadow: "-2px 0 16px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        <ResizeHandle onMouseDown={onStartResize} />
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.95rem 1.1rem",
            borderBottom: "1px solid #e9ecef",
            gap: "0.75rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933", flex: 1 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            <IconClose />
          </button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.45rem 0.6rem",
  fontSize: ".88rem",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  background: "#f8f9fb",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: "auto",
};

const closeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
